/**
 * Admin Login Page — matches the HomePage's cinematic aesthetic
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ImageSlideshow from '../components/ImageSlideshow';
import {
  Sparkles,
  User,
  Lock,
  AlertCircle,
  ArrowLeft,
  Loader2,
  Shield,
} from 'lucide-react';

const AdminLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Cinematic background — same slideshow as homepage */}
      <ImageSlideshow interval={5000} />

      {/* Dark gradient overlay — heavier than homepage for form readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/80" />

      {/* Subtle radial glow behind the card */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(168,85,247,0.15) 0%, transparent 70%)',
        }}
      />

      {/* ───── Content ───── */}
      <div className="relative z-10 w-full max-w-md px-5 py-10">
        {/* Brand pill — identical to homepage */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
            <Sparkles className="w-4 h-4 text-pink-300" />
            <span className="text-xs font-semibold text-white/90 tracking-widest uppercase">
              QuickVenue
            </span>
          </div>
        </div>

        {/* Heading — serif display font like homepage hero */}
        <h1
          className="font-display text-4xl md:text-5xl font-bold text-center leading-tight mb-2"
          style={{ textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}
        >
          <span className="text-white block">Admin</span>
          <span className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-300 via-pink-300 to-purple-200">
            Portal
          </span>
        </h1>

        {/* Accent bar */}
        <div className="h-1 w-16 bg-gradient-to-r from-purple-400 to-pink-400 mx-auto rounded-full mb-8" />

        {/* ───── Glass card ───── */}
        <div className="bg-white/[0.07] backdrop-blur-xl border border-white/[0.12] rounded-2xl p-8 shadow-2xl">
          {/* Card header */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-purple-300" />
            <h2 className="text-lg font-semibold text-white/90">Sign In</h2>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 bg-red-500/10 border border-red-400/20 rounded-xl px-4 py-3 mb-6">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-300">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2"
              >
                Username
              </label>
              <div
                className={`relative rounded-xl border transition-all duration-300 ${
                  focused === 'username'
                    ? 'border-purple-400/60 shadow-[0_0_20px_rgba(168,85,247,0.15)]'
                    : 'border-white/10'
                }`}
              >
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30 pointer-events-none" />
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={() => setFocused('username')}
                  onBlur={() => setFocused(null)}
                  className="w-full pl-12 pr-4 py-3.5 bg-white/[0.05] rounded-xl text-white placeholder-white/30 text-sm focus:outline-none"
                  placeholder="Enter your username"
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2"
              >
                Password
              </label>
              <div
                className={`relative rounded-xl border transition-all duration-300 ${
                  focused === 'password'
                    ? 'border-purple-400/60 shadow-[0_0_20px_rgba(168,85,247,0.15)]'
                    : 'border-white/10'
                }`}
              >
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30 pointer-events-none" />
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocused('password')}
                  onBlur={() => setFocused(null)}
                  className="w-full pl-12 pr-4 py-3.5 bg-white/[0.05] rounded-xl text-white placeholder-white/30 text-sm focus:outline-none"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-lg hover:shadow-purple-500/25"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Back link */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white/70 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Home
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-1.5">
          <p className="text-white/30 text-xs">Hall owners and managers only</p>
          <p className="text-white/20 text-xs">
            &copy; 2026 QuickVenue. All rights reserved.
          </p>
          <p className="text-white/20 text-xs">
            Designed & developed by{' '}
            <span className="text-purple-400/70 font-medium">Shrinidhi Katti</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
